// ---------------------------------------------------------------------------
// Maps a server finding to the form input it is about, by its FEMA status code (card FE-06). A
// server finding carries `field` (a CAP path such as `info.0.parameter.3.value`), `message`,
// `stage` and, since card BE-12, `code`. The CAP path does not name an input the operator can
// see, so the result modal names the input from this map. The status texts come from
// `config/femaStatusCodes.ts`.
// ---------------------------------------------------------------------------
import type { FemaOutcomeFieldFinding } from 'types/fema.types';
import { getFemaStatusInfo } from '../components/IpawsAlertFields/config/femaStatusCodes';

/** The form input a status code is about: its draft field path and its visible label. */
export type FemaFindingInput = {
  fieldPath: string;
  label: string;
};

const WEA_SHORT_TEXT: FemaFindingInput = {
  fieldPath: 'CMAMtext',
  label: 'WEA Message — 90 characters',
};
const WEA_HANDLING: FemaFindingInput = { fieldPath: 'WEAHandling', label: 'WEA handling' };
const EAS_ORG: FemaFindingInput = { fieldPath: 'EAS-ORG', label: 'EAS organization code' };
const EXPIRES: FemaFindingInput = { fieldPath: 'expires', label: 'Expiration time' };
const WEA_PRIORITY: FemaFindingInput = {
  fieldPath: 'urgency',
  label: 'Urgency, Severity and Certainty',
};
const RESPONSE_TYPE: FemaFindingInput = {
  fieldPath: 'responseType',
  label: 'Recommended Public Action',
};
const EVENT_CODE: FemaFindingInput = { fieldPath: 'eventCode', label: 'SAME event code' };
const AREA: FemaFindingInput = { fieldPath: 'geocodes', label: 'Authorized areas' };

/** Keyed by the FEMA status code as a string (the wire form). */
const FINDING_INPUT_BY_STATUS_CODE: Record<string, FemaFindingInput> = {
  '610': WEA_SHORT_TEXT,
  '622': WEA_HANDLING,
  '402': EAS_ORG,
  '502': EAS_ORG,
  // IDG Table 16 p29: EAS-ORG differs from the COG profile organization code.
  '312': EAS_ORG,
  '406': EXPIRES,
  '506': EXPIRES,
  '613': EXPIRES,
  '605': WEA_PRIORITY,
  '626': WEA_PRIORITY,
  '602': RESPONSE_TYPE,
  '415': EVENT_CODE,
  '515': EVENT_CODE,
  '615': EVENT_CODE,
  '419': AREA,
  '519': AREA,
  '619': AREA,
};

/**
 * The input a server finding is about, with FEMA's text for its status code, or `null` when the
 * finding has no code or the code is not in the map.
 */
export const getFemaFindingInput = (
  finding: Pick<FemaOutcomeFieldFinding, 'code'>,
): (FemaFindingInput & { statusRemark: string }) | null => {
  const code = typeof finding.code === 'string' ? finding.code.trim() : '';
  const input = code ? FINDING_INPUT_BY_STATUS_CODE[code] : undefined;
  if (!input) return null;
  return { ...input, statusRemark: getFemaStatusInfo(code).remark };
};
